import { useMemo } from 'react';

import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { ICartEntry } from 'features/cart/types';
import { isOfferCartEntry, isRewardCartEntry } from 'features/cart/utils';
import { useStoreContext } from 'features/store';
import { isEmpty } from 'lodash-es';

import { IMenuSelectionInput } from 'generated/graphql-gateway';
import { selectors, useAppSelector } from 'state/global-state';
import { getCmsOffersMapByCmsId } from 'state/loyalty/utils';
import { useServiceModeContext } from 'state/service-mode';

export const usePriceOrderV2Input = (
  menuSelections: IMenuSelectionInput[],
  cartEntriesParam?: ICartEntry[]
) => {
  const { store } = useStoreContext();
  const { serviceMode } = useServiceModeContext();
  const { cartEntries: minimalCartEntries } = useMinimalCart();

  const cmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const rewardsFeedbackMap = useAppSelector(selectors.loyalty.selectRewardsFeedbackMap);
  const cartEntries = cartEntriesParam ?? minimalCartEntries;

  const cmsOffersMap = useMemo(() => getCmsOffersMapByCmsId(cmsOffers), [cmsOffers]);

  const unappliedIncentiveEntryIds = useMemo(() => {
    const invalidEntryIds = new Set<string>();

    cartEntries.forEach(entry => {
      if (isOfferCartEntry(entry)) {
        const loyaltyEngineId = cmsOffersMap[entry.id]?.loyaltyEngineId ?? entry.referenceId;
        const feedback = loyaltyEngineId ? offersFeedbackMap[loyaltyEngineId] : undefined;

        // `useCartOfferValidation` considers an offer "applied" when feedback is empty.
        if (feedback && !isEmpty(feedback)) {
          invalidEntryIds.add(entry.id);
        }
        return;
      }

      if (isRewardCartEntry(entry)) {
        // Rewards feedback may be keyed by loyalty id/reference depending on how it was created.
        const possibleKeys = [
          entry.referenceId,
          entry.rewardIdReference,
          entry.id,
        ].filter((v): v is string => Boolean(v));
        const hasBlockingFeedback = possibleKeys.some(key => {
          const feedback = rewardsFeedbackMap[key];
          return feedback && !isEmpty(feedback);
        });

        if (hasBlockingFeedback) {
          invalidEntryIds.add(entry.id);
        }
      }
    });

    return invalidEntryIds;
  }, [cartEntries, cmsOffersMap, offersFeedbackMap, rewardsFeedbackMap]);

  const sanitizedMenuSelections = useMemo(
    () => menuSelections.filter(selection => !unappliedIncentiveEntryIds.has(selection.id)),
    [menuSelections, unappliedIncentiveEntryIds]
  );

  return {
    storeId: store.number,
    serviceMode,
    cart: { menuSelections: sanitizedMenuSelections },
  };
};
